
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Submission } from "@/types/submission";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import SubmissionDetails from "@/components/submission/SubmissionDetails";
import PrinterManufacturerSuggestionModal from "@/components/admin/PrinterManufacturerSuggestionModal";

interface PrinterManufacturerSuggestion {
  id: string;
  type: 'printer' | 'manufacturer';
  name: string;
  manufacturer_name?: string;
  series_name?: string;
  description?: string;
  build_plate_dimensions?: string;
  status: 'pending' | 'approved' | 'rejected';
  submitted_by: string;
  created_at: string;
  feedback?: string;
  manufacturer_id?: number;
  series_id?: number;
}

const AdminSubmissions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [printerSuggestions, setPrinterSuggestions] = useState<PrinterManufacturerSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("pending");
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<"approve" | "reject" | null>(null);
  const [currentSubmission, setCurrentSubmission] = useState<Submission | null>(null);
  const [feedback, setFeedback] = useState("");
  const [detailsSubmission, setDetailsSubmission] = useState<Submission | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<PrinterManufacturerSuggestion | null>(null);
  
  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching submissions with filter:', filter);
      
      let query = supabase.from("submissions").select("*");
      
      if (filter !== "all") {
        query = query.eq("status", filter);
      }
      
      const { data, error } = await query.order("submitted_at", { ascending: false });
      
      if (error) {
        console.error('Submissions query error:', error);
        throw error;
      }
      
      console.log('📋 Submissions fetched:', data?.length || 0, 'items');
      setSubmissions(data as Submission[]);
    } catch (error: any) {
      console.error('Full error details:', error);
      toast.error(`Error loading submissions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrinterSuggestions = async () => {
    try {
      setLoadingSuggestions(true);
      console.log('🏭 Fetching printer manufacturer suggestions with filter:', filter);
      
      let query = supabase.from("printer_manufacturer_suggestions").select("*");
      
      if (filter !== "all") {
        query = query.eq("status", filter);
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });
      
      if (error) {
        console.error('Printer suggestions query error:', error);
        throw error;
      }
      
      console.log('🏭 Printer suggestions fetched:', data?.length || 0, 'items');
      setPrinterSuggestions(data as PrinterManufacturerSuggestion[]);
    } catch (error: any) {
      console.error('Error fetching printer suggestions:', error);
      toast.error(`Error loading printer suggestions: ${error.message}`);
    } finally {
      setLoadingSuggestions(false);
    }
  };
  
  useEffect(() => {
    fetchSubmissions();
    fetchPrinterSuggestions();
  }, [filter]);
  
  const openActionDialog = (action: "approve" | "reject", submission: Submission) => {
    setCurrentAction(action);
    setCurrentSubmission(submission);
    setFeedback("");
    setIsActionDialogOpen(true);
  };
  
  const handleAction = async () => {
    if (!currentSubmission || !currentAction || !user) return;
    
    try {
      const { error } = await supabase
        .from("submissions")
        .update({
          status: currentAction === "approve" ? "approved" : "rejected",
          feedback: feedback || null,
          approved_by: currentAction === "approve" ? user.id : null,
          approved_at: currentAction === "approve" ? new Date().toISOString() : null
        })
        .eq("id", currentSubmission.id);
      
      if (error) throw error;
      
      toast.success(`Submission ${currentAction === "approve" ? "approved" : "rejected"} successfully`);
      setIsActionDialogOpen(false);
      fetchSubmissions();
      
    } catch (error: any) {
      toast.error(`Error ${currentAction}ing submission: ${error.message}`);
    }
  };

  const handlePrinterSuggestionAction = async (suggestionId: string, action: 'approve' | 'reject', feedback?: string) => {
    if (!user) return;

    try {
      if (action === 'approve') {
        const suggestion = printerSuggestions.find(s => s.id === suggestionId);
        if (!suggestion) throw new Error('Suggestion not found');

        if (suggestion.type === 'manufacturer') {
          const { error } = await supabase.rpc('approve_manufacturer_suggestion', {
            suggestion_id: suggestionId,
            approved_by_user: user.id
          });
          if (error) throw error;
        } else if (suggestion.type === 'printer') {
          const { error } = await supabase.rpc('approve_printer_suggestion', {
            suggestion_id: suggestionId,
            approved_by_user: user.id
          });
          if (error) throw error;
        }
        
        toast.success(`${suggestion.type} suggestion approved successfully`);
      } else {
        const { error } = await supabase
          .from("printer_manufacturer_suggestions")
          .update({
            status: 'rejected',
            feedback: feedback || null,
            reviewed_by: user.id,
            reviewed_at: new Date().toISOString()
          })
          .eq("id", suggestionId);

        if (error) throw error;
        toast.success('Suggestion rejected successfully');
      }

      fetchPrinterSuggestions();
      setSelectedSuggestion(null);
    } catch (error: any) {
      console.error(`Error ${action}ing suggestion:`, error);
      toast.error(`Failed to ${action} suggestion: ${error.message}`);
    }
  };

  const handleDeleteSuggestion = async (suggestionId: string) => {
    try {
      const { error } = await supabase
        .from("printer_manufacturer_suggestions")
        .delete()
        .eq("id", suggestionId);

      if (error) throw error;
      
      toast.success('Suggestion deleted successfully');
      fetchPrinterSuggestions();
      setSelectedSuggestion(null);
    } catch (error: any) {
      console.error('Error deleting suggestion:', error);
      toast.error(`Failed to delete suggestion: ${error.message}`);
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-100 dark:bg-yellow-900 border-yellow-500">Pending</Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-green-100 dark:bg-green-900 border-green-500">Approved</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-100 dark:bg-red-900 border-red-500">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  const filteredSubmissions = submissions.filter(sub => 
    filter === "all" ? true : sub.status === filter
  );

  const filteredSuggestions = printerSuggestions.filter(suggestion => 
    filter === "all" ? true : suggestion.status === filter
  );
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header useBackButton={true} pageName="Admin: Manage Submissions" />
      
      <div className="container py-8 pt-[64px]">
        <Card>
          <CardContent className="p-6">
            <Tabs defaultValue="pending" value={filter} onValueChange={(v) => setFilter(v as any)}>
              <div className="flex items-center justify-between mb-6">
                <TabsList>
                  <TabsTrigger value="pending">Pending</TabsTrigger>
                  <TabsTrigger value="approved">Approved</TabsTrigger>
                  <TabsTrigger value="rejected">Rejected</TabsTrigger>
                  <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
                
                <div className="text-sm text-muted-foreground">
                  {filteredSubmissions.length} submissions, {filteredSuggestions.length} printer/manufacturer suggestions
                </div>
              </div>

              {/* Submissions Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4">Tutorial & Part Submissions</h3>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-10">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : filteredSubmissions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-10">
                            No {filter === "all" ? "" : filter} submissions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSubmissions.map((submission) => (
                          <TableRow key={submission.id}>
                            <TableCell className="capitalize">{submission.type}</TableCell>
                            <TableCell className="font-medium">{submission.content.name}</TableCell>
                            <TableCell>
                              {formatDistanceToNow(new Date(submission.submitted_at), { addSuffix: true })}
                            </TableCell>
                            <TableCell>{getStatusBadge(submission.status)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setDetailsSubmission(submission)}
                                >
                                  View
                                </Button>
                                {submission.status === "pending" && (
                                  <>
                                    <Button 
                                      variant="default" 
                                      size="sm"
                                      onClick={() => openActionDialog("approve", submission)}
                                    >
                                      Approve
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => openActionDialog("reject", submission)}
                                      className="border-destructive text-destructive hover:bg-destructive/10"
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Printer/Manufacturer Suggestions Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Printer & Manufacturer Suggestions</h3>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Manufacturer</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loadingSuggestions ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : filteredSuggestions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10">
                            No {filter === "all" ? "" : filter} printer/manufacturer suggestions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSuggestions.map((suggestion) => (
                          <TableRow key={suggestion.id}>
                            <TableCell className="capitalize">{suggestion.type}</TableCell>
                            <TableCell className="font-medium">{suggestion.name}</TableCell>
                            <TableCell>{suggestion.manufacturer_name || '-'}</TableCell>
                            <TableCell>
                              {formatDistanceToNow(new Date(suggestion.created_at), { addSuffix: true })}
                            </TableCell>
                            <TableCell>{getStatusBadge(suggestion.status)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setSelectedSuggestion(suggestion)}
                                >
                                  View
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      {/* Action Dialog for regular submissions */}
      <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {currentAction === "approve" ? "Approve" : "Reject"} Submission
            </DialogTitle>
            <DialogDescription>
              {currentAction === "approve" 
                ? "The submission will be approved and made available to users." 
                : "Please provide feedback on why the submission is being rejected."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder={currentAction === "approve" 
                ? "(Optional) Add any comments or feedback"
                : "Please provide rejection reason"}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="min-h-[100px]"
              required={currentAction === "reject"}
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsActionDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant={currentAction === "approve" ? "default" : "destructive"}
              onClick={handleAction}
              disabled={(currentAction === "reject" && !feedback)}
            >
              {currentAction === "approve" ? "Approve" : "Reject"} Submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Details Dialog for regular submissions */}
      <Dialog open={!!detailsSubmission} onOpenChange={(open) => !open && setDetailsSubmission(null)}>
        <DialogContent className="max-w-3xl h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Submission Details
            </DialogTitle>
          </DialogHeader>
          
          {detailsSubmission && (
            <SubmissionDetails submission={detailsSubmission} />
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDetailsSubmission(null)}
            >
              Close
            </Button>
            {detailsSubmission?.status === "pending" && (
              <>
                <Button
                  variant="default"
                  onClick={() => {
                    setDetailsSubmission(null);
                    openActionDialog("approve", detailsSubmission);
                  }}
                >
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setDetailsSubmission(null);
                    openActionDialog("reject", detailsSubmission);
                  }}
                >
                  Reject
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Printer/Manufacturer Suggestion Modal */}
      {selectedSuggestion && (
        <PrinterManufacturerSuggestionModal
          suggestion={selectedSuggestion}
          isOpen={!!selectedSuggestion}
          onClose={() => setSelectedSuggestion(null)}
          onApprove={(id) => handlePrinterSuggestionAction(id, 'approve')}
          onReject={(id, feedback) => handlePrinterSuggestionAction(id, 'reject', feedback)}
          onDelete={handleDeleteSuggestion}
          onUpdate={() => fetchPrinterSuggestions()}
        />
      )}
    </div>
  );
};

export default AdminSubmissions;
